import { type CallingId, catalog, getItem, skill, type TownId, venuesIn } from '@wyn/content';
import { TERMS_VERSION } from '@wyn/contracts';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { IconCredit, LegalLinks, Masthead } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { Icon } from '../components/Icon.tsx';
import { AgreeToTerms, BirthMonth } from '../components/Terms.tsx';
import { useCreateCharacter } from '../lib/queries.ts';
import { TONE_TEXT, type Tone } from '../lib/theme.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

/** How each Calling's start fares in a fight, as the choice shows it. */
const FIGHTING: Record<'strong' | 'fair' | 'weak', { label: string; tone: string }> = {
  strong: { label: 'Strong', tone: TONE_TEXT['good' satisfies Tone] },
  fair: { label: 'Fair', tone: TONE_TEXT['warn' satisfies Tone] },
  weak: { label: 'Weak', tone: TONE_TEXT['bad' satisfies Tone] },
};

const STAT_LABEL = { strength: 'Strength', defence: 'Defence', speed: 'Speed', dexterity: 'Dexterity' } as const;

export function Create() {
  const me = useMe();
  const navigate = useNavigate();
  const create = useCreateCharacter();
  const [name, setName] = useState('');
  const [calling, setCalling] = useState<CallingId | null>(null);
  const [town, setTown] = useState<TownId | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [born, setBorn] = useState('');

  if (me.isPending) return <Loading />;
  if (me.data?.player) return <Navigate to="/town" />;

  const ready = name.trim().length >= 3 && calling && town && born && agreed;
  return (
    <div className="mx-auto max-w-4xl space-y-8 px-4 py-8">
      <Masthead />
      <form
        className="space-y-8"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          create.mutate({ name, calling, homeTown: town, born, termsVersion: TERMS_VERSION }, { onSuccess: () => navigate({ to: '/town' }) });
        }}
      >
        <section className="space-y-2">
          <h2 className="heading">I · Your name</h2>
          <input className="field w-full max-w-sm text-lg" value={name} onChange={(e) => setName(e.target.value)} maxLength={20} placeholder="Alys Fletcher" />
          <p className="text-sm text-ink-faint">3 to 20 letters. Spaces, hyphens and apostrophes allowed. Names are unique.</p>
        </section>

        <section className="space-y-3">
          <h2 className="heading">II · Your Calling</h2>
          <p className="text-sm text-ink-dim">Everyone can learn every skill. A Calling speeds three of them by 15% and grants a perk.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {catalog.callings.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setCalling(c.id)}
                aria-pressed={calling === c.id}
                className={`panel space-y-2 text-left transition-colors hover:border-ink-faint ${calling === c.id ? 'border-gilt!' : ''}`}
              >
                <div className="text-lg"><Icon name={c.icon} className="mr-2 text-gilt" />{c.name}</div>
                <div className="text-sm text-ink-dim">{c.affinity.map((s) => skill(s).name).join(' · ')}</div>
                <div className="text-sm"><span className="text-gilt">{c.perk.name}.</span> {c.perk.description}</div>
                <div className="text-sm">
                  <span className={FIGHTING[c.fighting.rating].tone}>{FIGHTING[c.fighting.rating].label} in a fight.</span> <span className="text-ink-dim">{c.fighting.note}</span>
                </div>
                <div className="text-xs text-ink-faint">
                  {c.kit.map((k) => `${getItem(k.item).name}${k.quantity > 1 ? ` ×${k.quantity}` : ''}`).join(', ')}
                  {c.bonusCoin > 0 && `, ${c.bonusCoin} extra coin`}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="heading">III · Your home town</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {catalog.towns.map((t) => (
              <button
                type="button"
                key={t.id}
                onClick={() => setTown(t.id)}
                aria-pressed={town === t.id}
                className={`panel space-y-1 text-left transition-colors hover:border-ink-faint ${town === t.id ? 'border-gilt!' : ''}`}
              >
                <div className="text-lg"><Icon name={t.icon} className="mr-2 text-gilt" />{t.name}{t.capital && <span className="ml-2 text-xs text-ink-faint">capital</span>}</div>
                <div className="text-sm text-ink-dim">{t.description}</div>
                <div className="text-xs text-ink-faint">Yard boosts {STAT_LABEL[t.yardStat]} · {venuesIn(t.id).map((v) => v.name).join(', ')}</div>
              </button>
            ))}
          </div>
        </section>

        <div className="space-y-3">
          <BirthMonth value={born} onChange={setBorn} />
          <AgreeToTerms agreed={agreed} onChange={setAgreed} />
          <button className="btn text-lg" type="submit" disabled={!ready || create.isPending}>
            {create.isPending ? 'Riding in…' : 'Begin →'}
          </button>
          <ErrorNotice error={create.error} />
        </div>
      </form>
      <footer className="flex flex-wrap gap-3 text-xs text-ink-faint"><LegalLinks /><IconCredit /></footer>
    </div>
  );
}
